import {Image, ImageOptimized} from '@/_application/core/components/ui/image';
import Link from '@/_application/core/components/ui/link';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import {getCategoryUrl} from '@/_application/modules/categories/models/url';
import {CategoryTree} from '__generated__/__types__';
import cn from 'classnames';
import {motion, useAnimation} from 'framer-motion';
import {useEffect, useState} from 'react';
import {getCategoryImageUrlByPath} from '@/_application/helpers/cdn-image';

const ViewMoreDisabledIcon = getImageAssetUrl('icons/icon-view-more-disabled.svg');
const ViewMoreIcon = getImageAssetUrl('icons/icon-view-more.svg');

interface IProps {
    categories: CategoryTree[];
}

function SubCategoryList({categories}: Readonly<IProps>) {
    const controls = useAnimation();
    const [isViewMore, setIsViewMore] = useState(false);

    useEffect(() => {
        controls.start({height: isViewMore ? '100%' : 160});
    }, [isViewMore]);

    if (categories?.length === 0) {
        return null;
    }

    return (
        <div className="relative mt-5 rounded-2xl bg-white p-5">
            <h3 className="text-[22px] font-extrabold leading-[25px] text-primary">Danh mục</h3>

            <div className="flex items-start">
                <motion.div
                    initial={{height: 160}}
                    className={cn('mt-4 flex flex-wrap items-start overflow-hidden', {
                        'w-[calc(100%-100px)] gap-4.5': categories?.length > 8,
                        'w-full gap-4': categories?.length <= 8,
                    })}
                    animate={controls}
                    transition={{duration: 0.4, ease: 'circOut'}}>
                    {categories.map(subCategory => {
                        const imageUrl = getCategoryImageUrlByPath(subCategory?.featured_icon!);
                        return (
                            <Link
                                href={getCategoryUrl(subCategory)}
                                key={subCategory?.id}
                                className="flex w-[130px] flex-col items-center justify-center gap-3 text-center transition-opacity hover:opacity-80">
                                <div className="flex h-[104px] w-[74px] items-center justify-center rounded-full border border-solid border-KPZ_turquoise">
                                    <div className="relative h-[60px] w-[60px]">
                                        <ImageOptimized
                                            src={imageUrl}
                                            alt={subCategory?.name!}
                                            fill
                                            sizes="100%"
                                            className="object-cover"
                                        />
                                    </div>
                                </div>
                                <span className="line-clamp-2 h-11 text-base leading-[22px] text-KPZ_text_1st">
                                    {subCategory?.name}
                                </span>
                            </Link>
                        );
                    })}
                </motion.div>

                {categories?.length > 8 && (
                    <button
                        aria-label="Xem thêm / Thu gọn thương hiệu"
                        className="mt-4 flex w-[130px] flex-col items-center justify-center gap-3 text-center"
                        onClick={() => setIsViewMore(prev => !prev)}>
                        <div
                            className={cn(
                                'flex h-[104px] w-[74px] items-center justify-center rounded-full border border-solid p-2',
                                {
                                    'border-KPZ_turquoise': isViewMore,
                                    'border-KPZ_Border': !isViewMore,
                                },
                            )}>
                            <div className="relative h-[48px] w-[48px]">
                                <Image
                                    src={isViewMore ? ViewMoreIcon : ViewMoreDisabledIcon}
                                    alt={'View more'}
                                    fill
                                    sizes="100%"
                                    className="object-cover"
                                />
                            </div>
                        </div>
                        <span className="text-base leading-[22px] text-KPZ_text_1st">Xem tất cả</span>
                    </button>
                )}
            </div>
        </div>
    );
}

export default SubCategoryList;
